import React, { useRef } from "react";
import { Copy, Download, ExternalLink, QrCode } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function PortalAccessActions({ portalUrl, name, testPrefix = "partner-portal", compact = false }) {
  const qrRef = useRef(null);
  if (!portalUrl) return null;
  const copyPortal = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(portalUrl);
      toast.success("Sub-portal link copied");
    } catch {
      const input = document.createElement("textarea");
      input.value = portalUrl;
      input.setAttribute("readonly", "");
      input.style.position = "fixed";
      input.style.opacity = "0";
      document.body.appendChild(input);
      input.select();
      const copied = document.execCommand("copy");
      input.remove();
      if (copied) toast.success("Sub-portal link copied");
      else toast.error("Copy the sub-portal link from the displayed URL");
    }
  };
  const downloadQr = () => {
    const svg = qrRef.current;
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 900; canvas.height = 900;
      const context = canvas.getContext("2d");
      context.fillStyle = "#ffffff"; context.fillRect(0, 0, 900, 900); context.drawImage(img, 0, 0, 900, 900);
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = `${(name || "distributing-partner").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-sub-portal-qr.png`;
      link.click();
    };
    img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(xml)))}`;
  };
  return (
    <div className={`border border-amber-200 bg-amber-50/60 ${compact ? "mt-3 rounded-md p-2.5" : "rounded-lg p-4"}`} data-testid={`${testPrefix}-access`}>
      <div className="flex gap-3">
        <div className="shrink-0 rounded-md border border-amber-200 bg-white p-1.5">
          <QRCodeSVG ref={qrRef} value={portalUrl} size={compact ? 72 : 104} level="M" bgColor="#ffffff" fgColor="#B01E24" data-testid={`${testPrefix}-qr`} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1 text-xs font-bold text-amber-950"><QrCode size={14} /> Your DP sub-portal</p>
          <code className="mt-1 block truncate text-[11px] text-gray-600" data-testid={`${testPrefix}-url`}>{portalUrl}</code>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Button size="sm" className="btn-brand h-7 text-xs" onClick={() => window.open(portalUrl, "_blank", "noopener,noreferrer")} data-testid={`${testPrefix}-open-button`}><ExternalLink size={12} className="mr-1" /> Open</Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={copyPortal} data-testid={`${testPrefix}-copy-button`}><Copy size={12} /></Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={downloadQr} data-testid={`${testPrefix}-download-button`}><Download size={12} /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}